import React, {useEffect, useRef} from 'react';
// Import Swiper React components
import {Swiper, SwiperSlide} from 'swiper/react';
// import required modules
import {Autoplay, FreeMode, Pagination, Navigation, Lazy} from 'swiper';
import type {Swiper as SwiperType} from 'swiper';
import styled from 'styled-components';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';

type Item<T> = T & {id: number | string};

export interface SwiperSliderProps<T> {
  items: Item<T>[];
  render: (item: Item<T>, index: number) => JSX.Element | null;
  slidesPerView?: number | string;
  spaceBetween?: number;
  slidesPerGroup?: number | string;
  autoHeight?: boolean;
  autoplay?: boolean;
  freeMode?: boolean;
  lastCard?: JSX.Element | null;
  dynamicBullets?: boolean;
  onReachEnd?: () => void;
  breakpoints?: Record<number, {slidesPerView?: number; spaceBetween?: number}>;
  pagination?: boolean;
  padding?: string;
}

const StyledSwiper = styled(Swiper)`
  width: 100%;
  z-index: 0;
  margin-top: 0px !important;
  padding: 4px;
  .swiper-pagination {
    position: relative !important;
    bottom: 0 !important;
    margin-top: 10px;
  }
`;

function SwiperSlider<T>({
  items,
  render,
  slidesPerView,
  spaceBetween,
  slidesPerGroup,
  autoHeight,
  autoplay,
  freeMode,
  lastCard,
  dynamicBullets,
  onReachEnd,
  breakpoints,
  pagination = true,
  padding,
}: SwiperSliderProps<T>) {
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    return () => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
    };
  }, []);

  // Swiper's autoHeight only re-measures on slideChange/init/resize, so the
  // wrapper height gets stuck when the active slide grows after first paint
  // (image loads, async hydration, etc.). Observe the active slide and force
  // a re-measure whenever its size actually changes.
  const observeActiveSlide = (swiper: SwiperType) => {
    if (!autoHeight) return;
    resizeObserverRef.current?.disconnect();
    const activeSlide = swiper.slides?.[swiper.activeIndex] as HTMLElement | undefined;
    if (!activeSlide) return;
    const ro = new ResizeObserver(() => swiper.updateAutoHeight());
    ro.observe(activeSlide);
    resizeObserverRef.current = ro;
  };

  if (!items) return null;

  return (
    <StyledSwiper
      //height="100%"
      padding="4px"
      freeMode={freeMode}
      autoHeight={autoHeight}
      autoplay={autoplay ? {delay: 8000} : false}
      watchSlidesProgress={true}
      slidesPerView={slidesPerView}
      spaceBetween={spaceBetween}
      slidesPerGroup={slidesPerGroup}
      loopedSlides={items.length}
      rewind={true}
      navigation={true}
      breakpoints={breakpoints}
      pagination={
        pagination
          ? {
              clickable: true,
              dynamicBullets: dynamicBullets,
            }
          : undefined
      }
      modules={[Autoplay, Pagination, Navigation, FreeMode, Lazy]}
      onSwiper={observeActiveSlide}
      onSlideChange={observeActiveSlide}
      onUpdate={observeActiveSlide}
      onReachEnd={onReachEnd}>
      {items.map((item, i) => {
        const rend = render(item, i);
        if (rend) {
          return (
            <SwiperSlide className="swiper-lazy" style={{width: 'fit-content'}} key={item.id}>
              {rend}
            </SwiperSlide>
          );
        }
      })}
      {lastCard && (
        <SwiperSlide style={{width: 'fit-content', height: '100%'}}>{lastCard}</SwiperSlide>
      )}
    </StyledSwiper>
  );
}

SwiperSlider.defaultProps = {
  slidesPerView: 1,
  spaceBetween: 10,
  slidesPerGroup: 1,
  autoPlay: false,
  freeMode: false,
  autoHeight: false,
  dynamicBullets: true,
  onReachEnd: undefined,
};

export default SwiperSlider;
